/*
	This hook returns a function that can be called to launch a file selection, without having to
	worry about placing a hidden file `input` in a component and rendering lifecycle, etc.

	This is suitable to use in buttons that immediately unmount, for example.

	It takes two arguments:

		`onChange` - The event handler for when the input's files change, which takes the
		             normal arguments for a `change` event.

		`inputProps` - Props that you want to pass to the input element, which forwards the keys:
			`multiple` - Set to `true` if you want to select multiple files.  Defaults to `false`.
			`accept` - A formatted string indicating what file types to accept. Defaults to any.

	For more details on inputProps, see the documentation for file inputs:
	https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file

	Example usage:
	```
		function UploadVideosButton(){
			const dispatch = useDispatch();
			let selectFiles = useFileSelector(
				(e) => {
					let fileList = e.currentTarget.files;
					let processedFiles = doSomethingToFiles(fileList);

					dispatch(uploadVideos(processedFiles));
				},
				{
					accept: "video/*",
					multiple: true,
				}
			);

			return (
				<Button onClick={selectFiles}>
					Upload Stuff
				</Button>
			);
		}
	```
*/

import { useEffect, useRef } from 'react';
import { noop } from "utils/common";

export default function useFileSelector(onChange = noop, inputProps = {}){
	let inputRef = useRef();

	function selectFiles(){
		inputRef.current?.click();
	}

	useEffect(() => {
		let newInput = document.createElement("input");
		let { multiple = false, accept = "" } = inputProps;

		newInput.type = "file";
		newInput.multiple = multiple;
		newInput.accept = accept;
		newInput.addEventListener("change", onChange);

		inputRef.current = newInput;
	}, []);

	return selectFiles;
}
